import commerceConfig from '@commerce/config'
import { Dollar } from '@components/icons'
import { useUI } from '@components/ui'
import useCustomer from '@lib/hooks/useCustomer'
import processLocale from '@lib/locale'
import { findBulkDiscountDetailsBySku } from '@lib/products'
import { useRouter } from 'next/router'

const BulkProductDiscount = ({
  variants,
  cartQuantity,
}: {
  variants: { sku: string; name: string }[]
  cartQuantity?: number
}) => {
  const {
    config: { context },
  } = commerceConfig
  const { locale } = useRouter()
  const { isB2B, customer } = useCustomer()
  const { setSidebarView, openSidebar, setBulkDiscountData } = useUI()
  const currentLocale = processLocale(locale)

  const companyMetafields =
    customer?.companyContacts?.edges?.[0]?.node?.company?.metafields
  const bulkDiscountCompanyGroup = companyMetafields?.find(
    (field) => field?.key === 'bulk_discount_group_new'
  )?.value

  // if customer is not b2b or if b2b but company does not have bulk discount group
  // then return null
  if (
    !isB2B ||
    !bulkDiscountCompanyGroup ||
    bulkDiscountCompanyGroup.length == 0
  )
    return null

  // get subset of variants with skus
  const skus = variants.map((variant) => variant.sku)

  // check current context is b2bBulkDiscount is enabled
  const currentContext = context?.[currentLocale]
  const isB2BBulkDiscountEnabled = currentContext?.b2bBulkDiscount

  let bDTag: string | null = null
  try {
    bDTag = JSON.parse(bulkDiscountCompanyGroup)[0]
  } catch (error) {
    console.error('Error parsing bulk discount group:', error)
    return null
  }

  if (!bDTag) return null

  const hasBulkDiscount = skus.filter((sku: string) => {
    const discountDetails = findBulkDiscountDetailsBySku(sku, bDTag!)
    return (
      discountDetails?.discountBreaks &&
      discountDetails?.discountBreaks?.length > 0
    )
  })
  // TODO: add check if isB2B is true
  // check bulk discount tag on customer : bdTags
  if (!isB2BBulkDiscountEnabled || hasBulkDiscount?.length === 0) {
    return null
  }

  if (cartQuantity) {
    const currentSku = skus?.[0]
    const discountDetails = findBulkDiscountDetailsBySku(currentSku, bDTag)

    const eligibleGroups = discountDetails?.discountBreaks?.filter(
      (d) => d.quantity > cartQuantity
    )

    const nextGroup =
      eligibleGroups &&
      eligibleGroups.length > 0 &&
      cartQuantity >= discountDetails?.discountBreaks?.[0].quantity!
        ? eligibleGroups?.[0]
        : discountDetails?.discountBreaks?.[0]

    if (!nextGroup || cartQuantity >= nextGroup.quantity) {
      return null
    }

    return (
      <div className=" mt-5 flex justify-between border border-ui-toast-green bg-white p-2 text-body2 text-ui-toast-green">
        <div className="flex gap-2">
          <Dollar />
          <span>{`Add ${
            nextGroup.quantity - cartQuantity
          } more to increase your discount to ${nextGroup.discount}%!`}</span>
        </div>
      </div>
    )
  }

  return (
    <div className=" mt-5 flex justify-between border border-ui-toast-green bg-white p-2 text-body2 text-ui-toast-green">
      <div className="flex gap-2">
        <Dollar />
        <span>Bulk buy discounts available</span>
      </div>
      <button
        className="underline hover:no-underline"
        onClick={() => {
          setSidebarView('BULK_DISCOUNT_SIDEBAR_VIEW')
          setBulkDiscountData({
            variants,
            discountGroup: bDTag,
          })
          openSidebar()
        }}
      >
        Learn more
      </button>
    </div>
  )
}

export default BulkProductDiscount
